import { useState, useEffect, useCallback } from 'react';
import { Modal, Input, Button, Form, message, Space } from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { FronteggContext } from '@frontegg/rest-api';

import { ZoomMtg } from '@zoom/meetingsdk';
import { InfoCircleIcon } from 'src/icons';
import participantZoom from 'src/assets/participantZoom.png';
import copyInvitation from 'src/assets/copyInvitation.png';
import { AxiosAmbient } from 'src/lib';
import { useSelector } from 'react-redux';
import { analytics } from 'src/services';
import { useAuth } from '@frontegg/react';

const ZoomMeetingModal = ({
  showZoomModal,
  setShowZoomModal,
  setupJoinButtonAutomation,
  handleCreateEncounter,
  onMeetingJoined,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [isZoomInitialized, setIsZoomInitialized] = useState(false);
  const [hasJoinedMeeting, setHasJoinedMeeting] = useState(false);
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { user } = useAuth();
  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate form
      const values = await form.validateFields();

      // Extract meeting details
      const meetingLink = values.meetingLink;
      if (!meetingLink) {
        message.error('Please enter a Zoom meeting link');
        return;
      }
      setLoading(true);

      // Track analytics event for zoom meeting submission
      analytics.track('Submitted Zoom Meeting Form', {
        email: user?.email,
        meetingName: values?.meetingName,
        hasMeetingLink: !!meetingLink,
      });

      const meetingDetails = extractMeetingDetails(meetingLink);

      if (!meetingDetails.meetingNumber) {
        message.error('Invalid Zoom meeting link format');
        return;
      }

      // Check if Zoom is connected via backend
      const authHeaders = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const connectionCheck = await AxiosAmbient.get(
        '/zoom-connection-status',
        {
          headers: authHeaders,
        }
      );

      if (!connectionCheck?.data?.connected) {
        message.error(
          'No Zoom authentication found. Please authenticate first.'
        );
        return;
      }

      // Try to start cloud recording to verify host access
      const canRecord = await startCloudRecording(meetingDetails.meetingNumber);

      if (!canRecord) {
        message.error(
          'Error. You need to be the meeting host to start a zoom encounter!'
        );
        setLoading(false);
        return;
      }

      const duplicateMeetingIdValidation = await AxiosAmbient.post(
        '/encounters/validate_duplicate_meeting_encounters',
        {
          zoom_meeting_id: meetingDetails.meetingNumber,
        },
        {
          headers: authHeaders,
        }
      );

      if (!duplicateMeetingIdValidation?.data?.success) {
        message.error(duplicateMeetingIdValidation?.data?.message);
        return;
      }

      // Start joining process
      message.loading({
        content: 'Preparing to join meeting...',
        key: 'joinMsg',
      });
      console.log('Meeting details:', meetingDetails);

      // Get signature from backend
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const requestBody = {
        meetingNumber: meetingDetails.meetingNumber,
        role: 0, // 1 for host
      };

      const result = await AxiosAmbient.post('/zoom-signature', requestBody, {
        headers,
      });

      const response = result?.data;
      console.log('signature response', response);

      if (!response?.signature) {
        throw new Error('Failed to generate meeting signature');
      }

      // Prepare to join meeting
      setJoining(true);

      // Join the meeting
      await joinZoomMeeting({
        meetingNumber: meetingDetails.meetingNumber,
        signature: response.signature,
        sdkKey: response.sdkKey,
        userName: 'AURA AI Scribe',
        password: meetingDetails.password,
      });
    } catch (error) {
      console.error('Error joining meeting:', error);
      message.error({
        content:
          'Failed to join meeting: ' + (error.message || 'Unknown error'),
        key: 'joinMsg',
      });
      setLoading(false);
      setJoining(false);
    } finally {
      setLoading(false);
    }
  };

  // Function to start cloud recording
  const startCloudRecording = async (meetingId) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const requestBody = {
        meeting_id: meetingId,
      };

      const result = await AxiosAmbient.post(
        '/zoom-start-recording',
        requestBody,
        {
          headers,
        }
      );

      const response = result?.data;
      return response?.success === true;
    } catch (error) {
      return false;
    }
  };

  // Function to check if user is authenticated via backend
  const checkZoomAuth = async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const result = await AxiosAmbient.get('/zoom-connection-status', {
        headers,
      });

      const response = result?.data;
      return {
        isAuthenticated: response?.connected === true,
        connected: response?.connected === true,
        expires_at: response?.expires_at,
        needs_reauth: response?.needs_reauth === true,
      };
    } catch (error) {
      console.error('Failed to check zoom auth:', error);
      return {
        isAuthenticated: false,
        connected: false,
        expires_at: null,
        needs_reauth: true,
      };
    }
  };

  // Extract meeting details from link
  const extractMeetingDetails = (link) => {
    try {
      // Meeting ID pattern
      const meetingIdMatch = link.match(/\/j\/(\d+)/);
      // Password pattern - handles both pwd= and pwd%3D formats
      const passwordMatch =
        link.match(/[?&]pwd=([^&]+)/) || link.match(/[?&]pwd%3D([^&]+)/);

      return {
        meetingNumber: meetingIdMatch ? meetingIdMatch[1] : null,
        password: passwordMatch ? decodeURIComponent(passwordMatch[1]) : null,
      };
    } catch (error) {
      console.error('Error extracting meeting details:', error);
      return { meetingNumber: null, password: null };
    }
  };

  // Clean up Zoom SDK
  const cleanupZoomSDK = useCallback(() => {
    try {
      if (isZoomInitialized && window.ZoomMtg) {
        console.log('Cleaning up Zoom SDK...');
        ZoomMtg.destroy();
        setIsZoomInitialized(false);
        setHasJoinedMeeting(false);
        console.log('Zoom SDK cleaned up successfully');
      }
    } catch (error) {
      console.error('Error cleaning up Zoom SDK:', error);
    }
  }, [isZoomInitialized]);

  // Function to leave meeting and clean up
  const leaveMeeting = useCallback(() => {
    try {
      if (isZoomInitialized && window.ZoomMtg) {
        console.log('Leaving meeting and cleaning up...');
        ZoomMtg.leaveMeeting({
          success: () => {
            console.log('Successfully left meeting');
            cleanupZoomSDK();
          },
          error: (error) => {
            console.error('Error leaving meeting:', error);
            cleanupZoomSDK(); // Clean up anyway
          },
        });
      } else {
        cleanupZoomSDK();
      }
    } catch (error) {
      console.error('Error in leaveMeeting:', error);
      cleanupZoomSDK();
    }
  }, [isZoomInitialized, cleanupZoomSDK]);

  // Join Zoom meeting
  const joinZoomMeeting = (params) => {
    console.log('Joining meeting with params:', {
      meetingNumber: params.meetingNumber,
      sdkKey: params.sdkKey,
      userName: params.userName,
      password: params.password ? '******' : 'none',
    });

    return new Promise((resolve, reject) => {
      // Clean up any existing Zoom SDK instance before initializing
      if (isZoomInitialized) {
        cleanupZoomSDK();
        // Add a small delay to ensure cleanup is complete
        setTimeout(() => {
          initializeZoomSDK(params, resolve, reject);
        }, 1000);
      } else {
        initializeZoomSDK(params, resolve, reject);
      }
    });
  };

  // Initialize Zoom SDK
  const initializeZoomSDK = (params, resolve, reject) => {
    ZoomMtg.init({
      leaveUrl: import.meta.env.VITE_AMBIENT_UI_BASE_URL,
      isSupportAV: false, // Disable Audio/Video completely
      disableJoinAudio: true,
      success: () => {
        console.log('Zoom SDK initialized successfully');
        setIsZoomInitialized(true);
        console.log('params', params);

        ZoomMtg.join({
          sdkKey: params.sdkKey,
          signature: params.signature,
          meetingNumber: params.meetingNumber,
          passWord: params.password,
          userName: 'Aura AI Scribe',
          success: async (joinResult) => {
            console.log('Join command successful', joinResult);
            setHasJoinedMeeting(true); // Mark that we've successfully joined

            // Track successful zoom meeting join
            analytics.track('Successfully Joined Zoom Meeting', {
              email: user?.email,
              meetingNumber: params?.meetingNumber,
              meetingName: form.getFieldValue('meetingName'),
            });

            // Create encounter after successfully joining the meeting
            if (handleCreateEncounter && params.meetingNumber) {
              const meetingName = form.getFieldValue('meetingName');
              const customOptions = {
                description:
                  meetingName || `Zoom Meeting - ${params.meetingNumber}`,
                zoom_meeting_id: params.meetingNumber,
                callback: (createdEncounter) => {
                  console.log(
                    'Encounter created with zoom meeting ID:',
                    createdEncounter
                  );
                },
              };

              try {
                await handleCreateEncounter(
                  currentPractitioner?.practitioner_id,
                  customOptions
                );
                message.success(
                  'AURA AI has joined the meeting. The transcription will be available after the meeting ends.'
                );
                // Trigger the alert modal after successful join
                if (onMeetingJoined) {
                  onMeetingJoined();
                }
              } catch (error) {
                console.error('Failed to create encounter:', error);
                message.error(
                  'Meeting joined successfully, but failed to create encounter'
                );
              }
            }

            resolve(joinResult);
            setLoading(false);
          },
          error: (error) => {
            console.error('Error joining meeting:', error);

            // Track failed zoom meeting join
            analytics.track('Failed to Join Zoom Meeting', {
              email: user?.email,
              meetingNumber: params?.meetingNumber,
              errorMessage: error.errorMessage || JSON.stringify(error),
            });

            cleanupZoomSDK(); // Clean up on error
            reject(error);
            setLoading(false);
            setJoining(false);
            message.error({
              content:
                'Failed to join meeting: ' +
                (error.errorMessage || JSON.stringify(error)),
              key: 'joinMsg',
            });
          },
        });
        setTimeout(() => {
          setupJoinButtonAutomation();
        }, 3000);

        setShowZoomModal(false);
      },
      error: (error) => {
        console.error('Error initializing Zoom:', error);

        // Track failed zoom SDK initialization
        analytics.track('Failed to Initialize Zoom SDK', {
          email: user?.email,
          errorMessage: error.errorMessage || JSON.stringify(error),
        });

        cleanupZoomSDK(); // Clean up on initialization error
        reject(error);
        setLoading(false);
        setJoining(false);
        message.error({
          content:
            'Failed to initialize Zoom: ' +
            (error.errorMessage || JSON.stringify(error)),
          key: 'joinMsg',
        });
      },
    });
  };

  // Handle cancel/close
  const handleCancel = () => {
    if (!loading && !joining) {
      // Track zoom meeting modal cancellation
      analytics.track('Cancelled Zoom Meeting Modal', {
        email: user?.email,
      });

      // Only cleanup if we haven't successfully joined a meeting yet
      // If we've joined, the SDK should remain active for the meeting
      if (!hasJoinedMeeting) {
        cleanupZoomSDK(); // Only clean up if we haven't joined the meeting
      }
      form.resetFields();
      setShowZoomModal(false);
    }
  };

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const authStatus = await checkZoomAuth();
      console.log('Zoom authentication status:', authStatus);
    };

    checkAuthStatus();
  }, []);

  // Cleanup Zoom SDK when component unmounts
  useEffect(() => {
    return () => {
      // Only cleanup if we haven't joined a meeting
      // If we've joined, the SDK should remain active
      if (!hasJoinedMeeting) {
        cleanupZoomSDK();
      }
    };
  }, [cleanupZoomSDK, hasJoinedMeeting]);

  // Expose cleanup functions globally for external access
  useEffect(() => {
    // Expose cleanup functions globally for external access
    window.cleanupZoomSDK = cleanupZoomSDK;
    window.leaveZoomMeeting = leaveMeeting;

    return () => {
      // Clean up global references
      delete window.cleanupZoomSDK;
      delete window.leaveZoomMeeting;
    };
  }, [cleanupZoomSDK, leaveMeeting]);

  return (
    <Modal
      title="Add to live meeting"
      open={showZoomModal}
      onCancel={handleCancel}
      footer={null}
      width={600}
      maskClosable={!loading}
      closable={!loading}
      destroyOnClose={true}
      className="zoom-meeting-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Name your encounter (Optional)" name="meetingName">
          <Input placeholder="Title" disabled={loading} />
        </Form.Item>

        <Form.Item
          label="Meeting link"
          name="meetingLink"
          rules={[
            { required: true, message: 'Please enter a meeting link' },
            {
              pattern: /zoom\.us\/j\/\d+/,
              message: 'Please enter a valid Zoom meeting link',
            },
          ]}
          // extra="Capture meetings from Zoom, MS teams, and more."
        >
          <Input
            prefix={<LinkOutlined />}
            placeholder="https://zoom.us/j/1234567890"
            disabled={loading}
          />
        </Form.Item>
        <div className=" w-full my-3">
          <button
            className="underline text-[#001bde] flex justify-between items-center w-full p-0 "
            onClick={() => setIsInstructionsOpen(!isInstructionsOpen)}
          >
            <div className=" flex items-center gap-1 text-sm">
              <InfoCircleIcon size={14} className="size-4" />
              How to enable audio transcription
            </div>
          </button>

          {isInstructionsOpen && (
            <div className="mt-2 p-4 bg-green-50 rounded-lg">
              <h3 className="font-medium text-sm text-gray-800 mb-3">
                How to Copy Zoom Meeting Link:
              </h3>

              <div className="flex mb-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 flex items-center justify-center mr-3">
                  <span className="text-[#00d091] text-sm">1</span>
                </div>
                <div>
                  <p className="text-gray-800 font-medium text-sm">
                    Open your Zoom meeting or scheduled meeting
                  </p>
                  <p className="text-[#000000E0] flex items-start text-xs bg-white p-2 rounded-md">
                    <LinkOutlined size={12} className="mr-2 mt-1" />
                    Start or join your Zoom meeting first
                  </p>
                </div>
              </div>

              <div className="flex mb-4">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 flex items-center justify-center mr-3">
                  <span className="text-[#00d091] text-sm">2</span>
                </div>
                <div>
                  <p className="text-gray-800 font-medium text-sm">
                    Click on &quot;Participants&quot; or &quot;Invite&quot;
                    button
                  </p>
                  <p className="text-[#000000E0] flex items-start text-xs bg-white p-2 rounded-md">
                    <span className="mr-2">👥</span>
                    Look for the invite option in your Zoom controls
                  </p>
                  <div
                    className="w-full overflow-hidden rounded border border-gray-200"
                    style={{ maxHeight: '120px' }}
                  >
                    <img
                      src={participantZoom}
                      alt="Participant"
                      className="w-full object-cover object-top"
                      style={{ maxHeight: '120px' }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-200 flex items-center justify-center mr-3">
                  <span className="text-[#00d091]">3</span>
                </div>
                <div>
                  <p className="text-gray-800 font-medium text-sm">
                    Copy the meeting link and paste it here
                  </p>
                  <p className="text-[#000000E0] flex items-start text-xs bg-white p-2 rounded-md">
                    <CopyOutlined size={14} className="mr-2 mt-[2px]" />
                    AURA AI bot will join to start cloud recording
                  </p>
                  <div
                    className="w-full overflow-hidden rounded border border-gray-200"
                    style={{ maxHeight: '120px' }}
                  >
                    <img
                      src={copyInvitation}
                      alt="InviteClicked"
                      className="w-full object-cover object-top"
                      style={{ maxHeight: '120px' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Form.Item className="modal-footer">
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={joining}
            >
              {joining ? 'Joining...' : 'Start Capturing'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ZoomMeetingModal;
